import { ForwardedRef, forwardRef } from 'react';
import cn from 'classnames';
import { matchPath, generatePath } from 'react-router-dom';
import ArrowTipRightIcon from '@stripcash/ui-kit/icons/arrowTipRight.svg';
import ArrowTipLeftIcon from '@stripcash/ui-kit/icons/arrowTipLeft.svg';
import {
  HorizontalSeparator,
  SeparatorSize,
} from '@stripcash/ui-kit/components/Separator';

import { Category } from 'common/selectors/sidebar/utils';
import { useT } from 'common/i18n';
import { SIDEBAR_FOOTER_ID } from 'common/constants/ui';

import SidebarButton from '../SidebarButton';
import checkIsNewSubgroup from '../../utils/checkIsNewSubgroup';

import styles from './styles.module.css';
import { icons, TOOLTIP_STRING_LIMITS } from './constants';
import ParentTab from './components/ParentTab';
import SubgroupTab from './components/SubTabComponent';
import TabGroupWrapper from './components/TooltipWrapper';

export type SidebarViewProps = {
  structure: Category[];
  isSidebarShown: boolean;
  collapsed: boolean;
  activeGroup: string;
  pathname: string;
  toggleCollapsed: () => void;
  dangers: Record<string, boolean | number>;
  isMobile: boolean;
  expandedGroups: string[];
  onExpand: (group: string) => void;
};

const SidebarView = forwardRef(
  (
    {
      isSidebarShown,
      collapsed,
      structure,
      activeGroup,
      pathname,
      dangers,
      toggleCollapsed,
      isMobile,
      expandedGroups,
      onExpand,
    }: SidebarViewProps,
    ref: ForwardedRef<HTMLElement>,
  ) => {
    const t = useT('sidebar');

    return (
      <nav
        ref={ref}
        className={cn(styles.sidebar, {
          [styles.sidebarShown]: isSidebarShown,
          [styles.sidebarCollapsed]: collapsed,
        })}
        id="sidebarPopover"
      >
        <div
          className={cn(styles.sidebarContent, {
            [styles.sidebarContentCollapsed]: collapsed,
          })}
        >
          {structure.map(({ title, path, sections = [], icon }) => {
            const Icon = icons[icon];
            const subGroups = sections.filter(
              (section) => section.path !== path,
            ); // do not render sections with same path as root (wl case)
            const isParentGroup =
              subGroups?.length > 0 && title[0] !== subGroups[0].title;
            const isActiveGroup = expandedGroups.includes(title[0]);
            const hasNewSubgroups = subGroups.some(({ path }) =>
              checkIsNewSubgroup(path),
            );

            return (
              <div className={styles.groupWrapper} key={path}>
                <TabGroupWrapper
                  title={title[0]}
                  hasTooltip={
                    title[0]?.length > TOOLTIP_STRING_LIMITS.rootGroup ||
                    collapsed
                  }
                >
                  <ParentTab
                    isParentGroup={isParentGroup}
                    isActiveGroup={isActiveGroup}
                    activeGroup={activeGroup}
                    hasNewSubgroups={hasNewSubgroups}
                    expandedGroups={expandedGroups}
                    title={title[0]}
                    collapsed={collapsed}
                    onExpand={onExpand}
                    path={path}
                    href={generatePath(sections[0].path)} // always lead to first category section path
                    Icon={Icon}
                  />
                </TabGroupWrapper>

                {isActiveGroup &&
                  isParentGroup &&
                  subGroups.map((section) => (
                    <TabGroupWrapper
                      key={section.path}
                      title={section.title}
                      hasTooltip={
                        section.title?.[0]?.length >
                          TOOLTIP_STRING_LIMITS.parentGroup || collapsed
                      }
                    >
                      <SubgroupTab
                        title={section.title}
                        path={section.path}
                        href={generatePath(section.path)}
                        isSubgroupActive={Boolean(
                          matchPath(pathname, section.path),
                        )}
                        collapsed={collapsed}
                        dangers={dangers}
                        newLabelText={t('new')}
                      />
                    </TabGroupWrapper>
                  ))}
              </div>
            );
          })}
        </div>

        <div id={SIDEBAR_FOOTER_ID} className={styles.sidebarFooter}>
          {isMobile && <HorizontalSeparator size={SeparatorSize.None} />}
          {!isMobile && (
            <>
              <HorizontalSeparator size={SeparatorSize.None} />
              <SidebarButton
                collapsed={collapsed}
                onClick={toggleCollapsed}
                title={t('collapseSidebar')}
                icon={collapsed ? <ArrowTipRightIcon /> : <ArrowTipLeftIcon />}
              />
            </>
          )}
        </div>
      </nav>
    );
  },
);

export default SidebarView;
